import axios, { type AxiosInstance, isAxiosError } from "axios";
import i18n from "../locales/i18n";
import { config } from "../config";
import { vgsPostUrl } from "../utils/tokenization-config";
import type {
  IWidgetSession,
  ITermsBlock,
} from "../shared/session/interfaces/session";
import type {
  ICardPaymentMethod,
  IAllPaymentMethods,
  IAddCardPaymentMethodRequest,
  IPreValidateCardRequest,
  IPreValidateCardResult,
  ICardUsageReadinessInput,
  ICardUsageReadinessResult,
  ICardProvisionInput,
  ICardProvisionResult,
} from "../interfaces/payment-method";
import { LINK_ERROR_CODE, RequestStatus } from "../utils/commons";
import { resolveUrlLinkId } from "../utils/resolveUrlLinkId";
import { resolveXLinkIdHeader } from "./resolveXLinkIdHeader";
import { useSessionStore } from "../store/useSessionStore";
import { useOfflineStore } from "../store/useOfflineStore";
import { isAxiosNetworkFailure } from "../utils/axiosNetworkError";

/**
 * Error class for link claim errors
 */
export class LinkClaimError extends Error {
  constructor(
    message: string,
    public code: LINK_ERROR_CODE,
    public statusCode?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = "LinkClaimError";
  }
}

export interface WidgetApiError {
  code: string;
  message: string;
  details?: unknown;
}

/** Extracts the normalized `{ code, message }` payload from a widget API error. */
export const getWidgetApiError = (error: unknown): WidgetApiError | null => {
  if (!isAxiosError(error)) return null;
  const body = error.response?.data?.error;
  if (!body || typeof body.code !== "string") return null;
  return {
    code: body.code,
    message: typeof body.message === "string" ? body.message : "",
    details: body.details,
  };
};

/**
 * Codes that make the link permanently unusable: the widget cannot recover from
 * them and must show the matching explanation instead of a generic error.
 */
const DEAD_LINK_CODES: string[] = [
  LINK_ERROR_CODE.LINK_NOT_FOUND,
  LINK_ERROR_CODE.LINK_EXPIRED,
  LINK_ERROR_CODE.LINK_REVOKED,
];

/**
 * Resolves the claim/API failure into a link error code. The backend `code`
 * wins over the HTTP status: a revoked link answers 409 `LINK_REVOKED` on
 * claim and 403 `LINK_REVOKED` on the partner CIP ticket, so status alone
 * cannot tell "revoked" from "already used".
 */
const resolveLinkErrorCode = (
  status: number | undefined,
  apiCode: string | undefined
): LINK_ERROR_CODE => {
  if (apiCode && apiCode in LINK_ERROR_CODE) {
    return apiCode as LINK_ERROR_CODE;
  }

  switch (status) {
    case 400:
      return LINK_ERROR_CODE.INVALID_TOKEN;
    case 404:
      return LINK_ERROR_CODE.LINK_NOT_FOUND;
    case 409:
      return LINK_ERROR_CODE.LINK_ALREADY_USED;
    case 410:
      return LINK_ERROR_CODE.LINK_EXPIRED;
    case 429:
      return LINK_ERROR_CODE.RATE_LIMITED;
    default:
      // No status means the request never reached the backend (network/CORS).
      return status === undefined || status >= 500
        ? LINK_ERROR_CODE.SERVICE_UNAVAILABLE
        : LINK_ERROR_CODE.INTERNAL_ERROR;
  }
};

/**
 * Custom event for dead links (LINK_NOT_FOUND, LINK_EXPIRED, LINK_REVOKED).
 * Dispatched when the API returns these errors on any widget endpoint.
 */
export const SESSION_EXPIRED_EVENT =
  "raas-widgets-flow-deferred-payout-origination-session-expired";

export const MAINTENANCE_EVENT = "raas-widgets-flow-deferred-payout-origination-maintenance";

export interface MaintenanceStatus {
  active: boolean;
  title?: string;
  body?: string;
  supportEmail?: string;
  supportWhatsappNumber?: string;
}
export const USER_BLOCKED_EVENT =
  "raas-widgets-flow-deferred-payout-origination-user-blocked";

/**
 * True when the failure means the link itself died (expired / revoked / gone).
 * `useSessionExpiredHandler` already routes those to the explanatory screen, so
 * callers must not overwrite it with their own generic error copy.
 */
export const isDeadLinkError = (error: unknown): boolean => {
  if (!isAxiosError(error)) return false;
  const code = error.response?.data?.error?.code;
  return DEAD_LINK_CODES.includes(code) || error.response?.status === 410;
};

/**
 * Current link ID for API requests. Set after a successful claim.
 */
let currentLinkId: string | null = null;

/**
 * Set the current link ID for API requests (adds the x-link-id header).
 */
export const setWidgetLinkId = (linkId: string | null) => {
  currentLinkId = linkId;
};

export const getWidgetLinkId = (): string | null => currentLinkId;

export const probeLinkSession = async (
  linkId: string
): Promise<{ blocked: boolean; expiresAt: string | null }> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/session`, {
    raasSilentOffline: true,
  });
  const expiresAt = data?.data?.expiresAt;
  return {
    blocked: Boolean(
      data?.data?.userStatus?.blocked ?? data?.data?.user?.blocked
    ),
    expiresAt: typeof expiresAt === "string" ? expiresAt : null,
  };
};

export const fetchMaintenanceStatus = async (
  linkId: string,
  locale: string,
): Promise<MaintenanceStatus> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/maintenance`,
    { params: { locale }, raasSilentOffline: true },
  );
  const payload = data?.data;
  if (!payload?.active) return { active: false };
  return {
    active: true,
    title: typeof payload.title === "string" ? payload.title : undefined,
    body: typeof payload.body === "string" ? payload.body : undefined,
    supportEmail:
      typeof payload.supportEmail === "string" ? payload.supportEmail : undefined,
    supportWhatsappNumber:
      typeof payload.supportWhatsappNumber === "string"
        ? payload.supportWhatsappNumber
        : undefined,
  };
};

export const dispatchSessionExpiredEvent = (detail: {
  code: string;
  details?: unknown;
}): void => {
  window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
};

function extractRequestLinkId(req: { headers?: unknown; url?: string } | undefined): string | null {
  if (!req) return null;
  const headers = req.headers as Record<string, unknown> | undefined;
  const header = headers?.["x-link-id"] ?? headers?.["X-Link-Id"];
  if (typeof header === "string" && header.length > 0) return header;
  const match = (req.url ?? "").match(/\/widget\/links\/([^/?]+)/);
  return match?.[1] ?? null;
}

export function shouldBroadcastSessionExpired(req: {
  headers?: unknown;
  url?: string;
} | undefined): boolean {
  if (useSessionStore.getState().session.status === RequestStatus.LOADING) {
    return false;
  }
  const reqLinkId = extractRequestLinkId(req);
  const urlLinkId = resolveUrlLinkId();
  if (reqLinkId && urlLinkId && reqLinkId !== urlLinkId) {
    return false;
  }
  return true;
}

/** ngrok free returns an HTML interstitial unless this header is present. */
const isNgrokBaseUrl = (url: string): boolean =>
  /ngrok(-free)?\.(app|dev)/i.test(url);

const applyNgrokDevHeaders = (
  headers: Record<string, string>,
  baseUrl: string
): void => {
  if (isNgrokBaseUrl(baseUrl)) {
    headers["ngrok-skip-browser-warning"] = "true";
  }
};

/**
 * Axios instance for authenticated widget API calls (x-link-id header).
 */
const createWidgetApiInstance = (): AxiosInstance => {
  const instance = axios.create({
    baseURL: config.API_BASE_URL,
  });

  instance.interceptors.request.use((requestConfig) => {
    applyNgrokDevHeaders(
      requestConfig.headers as Record<string, string>,
      config.API_BASE_URL
    );
    // Bypass a disk-cached 410 on header-only GETs (catalog URL has no linkId).
    const method = (requestConfig.method ?? "get").toLowerCase();
    if (method === "get") {
      requestConfig.headers["Cache-Control"] = "no-cache";
      requestConfig.headers["Pragma"] = "no-cache";
    }
    const urlLinkId = resolveUrlLinkId();
    const persisted =
      useSessionStore.getState().session.data?.linkId ?? null;
    if (urlLinkId && currentLinkId !== urlLinkId) {
      setWidgetLinkId(urlLinkId);
    }
    const header = resolveXLinkIdHeader({
      urlLinkId,
      currentLinkId,
      persistedLinkId: persisted,
    });
    if (header) {
      requestConfig.headers["x-link-id"] = header;
    }
    return requestConfig;
  });

  instance.interceptors.response.use(
    (response) => response,
    (error) => {
      const errorCode = error?.response?.data?.error?.code;
      const statusCode = error?.response?.status;

      if (errorCode === "USER_BLOCKED") {
        window.dispatchEvent(new CustomEvent(USER_BLOCKED_EVENT));
      }

      if (errorCode === LINK_ERROR_CODE.SERVICE_UNAVAILABLE_MAINTENANCE) {
        window.dispatchEvent(
          new CustomEvent(MAINTENANCE_EVENT, {
            detail: error?.response?.data?.error?.details,
          }),
        );
      }

      if (
        (DEAD_LINK_CODES.includes(errorCode) || statusCode === 410) &&
        shouldBroadcastSessionExpired(error?.config)
      ) {
        window.dispatchEvent(
          new CustomEvent(SESSION_EXPIRED_EVENT, {
            detail: {
              code: errorCode || LINK_ERROR_CODE.LINK_EXPIRED,
              details: error?.response?.data?.error?.details,
            },
          })
        );
      }

      if (
        isAxiosNetworkFailure(error) &&
        error.config?.raasSilentOffline !== true
      ) {
        useOfflineStore.getState().reportOffline();
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const widgetApi = createWidgetApiInstance();

export interface NotificationPreferenceChannel {
  channel: "in_app" | "email" | "sms";
  enabled: boolean;
}

export interface SmsConsentEntry {
  id: string;
  introMarkdown: string;
  detailTitle?: string;
  detailMarkdown: string;
}

export interface SmsConsentData {
  available: boolean;
  version?: string;
  maskedPhone?: string;
  current: "accepted" | "declined" | "none";
  language?: string;
  entries: SmsConsentEntry[];
}

export const getSmsConsent = async (
  linkId: string,
  lang: string,
): Promise<SmsConsentData> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/sms-consent`, {
    params: { lang },
  });
  return data.data;
};

/** Records Accept or Not now. Returns the refreshed preference map. */
export const decideSmsConsent = async (
  linkId: string,
  input: {
    decision: "accepted" | "declined";
    version: string;
    language: string;
  },
): Promise<NotificationPreferenceChannel[]> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/sms-consent`,
    input,
  );
  return data.data.channels;
};

export const getNotificationPreferences = async (
  linkId: string,
): Promise<NotificationPreferenceChannel[]> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/notification-preferences`,
  );
  return data.data.channels;
};

export const saveNotificationPreferences = async (
  linkId: string,
  channels: NotificationPreferenceChannel[],
): Promise<NotificationPreferenceChannel[]> => {
  const { data } = await widgetApi.put(
    `/widget/links/${linkId}/notification-preferences`,
    { channels },
  );
  return data.data.channels;
};

export const saveContactPhone = async (
  linkId: string,
  phone: string,
  locale: string
): Promise<{ hasPhoneNumber: boolean; phoneCountry?: string }> => {
  const { data } = await widgetApi.put(
    `/widget/links/${linkId}/contact/phone`,
    { phone, locale }
  );
  return data.data;
};


/**
 * Issue a live corridor document (PDF receipt) for the bound operation.
 * GET /widget/links/:linkId/operations/:operationId/documents/:kind
 */
export const fetchOperationDocument = async (
  linkId: string,
  operationId: string,
  kind: string
): Promise<Blob> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/operations/${operationId}/documents/${kind}`,
    { responseType: "blob" }
  );
  return data;
};

export interface ICancelOperationResponse {
  operationId: string;
  status: string;
  cancellationReason: string;
  mode?: "void" | "request";
  corridorOutcome?: "ok" | "failed" | "not_applicable";
}

/** POST /widget/links/:linkId/operations/:operationId/cancel */
export const cancelOperation = async (
  linkId: string,
  operationId: string
): Promise<ICancelOperationResponse> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/operations/${operationId}/cancel`
  );
  return data.data;
};

/**
 * Device info to send with the claim request.
 */
export interface IDeviceInfo {
  userAgent?: string;
  language?: string;
  timezone?: string;
}

/**
 * Claim a magic link by linkId.
 * POST /widget/links/:linkId/claim
 *
 * The response contains the partner configuration — including the theme that
 * the widget must apply and the link-scoped presentation flags
 * (`config.showSplash`; `config.showAgreement` is deprecated and ignored).
 */
export const claimLink = async (
  linkId: string,
  deviceInfo?: IDeviceInfo,
  lang?: string,
  seonSession?: string
): Promise<IWidgetSession> => {
  const apiBaseUrl = config.API_BASE_URL;

  try {
    const claimHeaders: Record<string, string> = {};
    applyNgrokDevHeaders(claimHeaders, apiBaseUrl);
    const { data } = await axios.post(
      `${apiBaseUrl}/widget/links/${linkId}/claim`,
      {
        deviceInfo,
        ...(seonSession ? { seonSession } : {}),
      },
      {
        params: lang ? { lang } : undefined,
        headers: claimHeaders,
      }
    );

    return {
      sessionId: data.data.sessionId,
      linkId: data.data.linkId,
      token: data.data.token,
      status: data.data.status,
      expiresAt: data.data.expiresAt,
      partner: {
        partnerId: data.data.partner.partnerId,
        name: data.data.partner.name,
        primaryColor: data.data.partner.primaryColor,
        theme: data.data.partner.theme,
        supportEmail: data.data.partner.supportEmail,
        supportWhatsappNumber: data.data.partner.supportWhatsappNumber,
        sessionExpiredRedirectUrl: data.data.partner.sessionExpiredRedirectUrl,
      },
      userData: data.data.userData,
      user: data.data.user,
      userStatus: data.data.userStatus,
      config: data.data.config,
      language: data.data.language,
      flavor: data.data.flavor,
      themeMode: data.data.themeMode,
      termsAccepted: data.data.termsAccepted,
      termsAcceptedAt: data.data.termsAcceptedAt,
      terms: data.data.terms,
      ...(data.data.novu ? { novu: data.data.novu } : {}),
    };
  } catch (error) {
    const status = isAxiosError(error) ? error.response?.status : undefined;
    const apiError = getWidgetApiError(error);
    const code = resolveLinkErrorCode(status, apiError?.code);

    // The message is only a diagnostic fallback: the error screen resolves the
    // user-facing copy from `code` via i18n.
    throw new LinkClaimError(
      apiError?.message || `Failed to claim link (${code})`,
      code,
      status,
      apiError?.details,
    );
  }
};

/**
 * Record the Terms & Conditions / E-Sign acceptance for the current user.
 * Persisted at the user level by the backend so the same user is not prompted
 * again on future links.
 * POST /widget/links/:linkId/accept-terms
 */
export interface IAcceptTermsResponse {
  termsAccepted: boolean;
  termsAcceptedAt?: string;
  termsAcceptedVersion?: string;
}

export const acceptTerms = async (
  linkId: string,
  options?: {
    version?: string;
    documents?: string[];
    acceptedEntryIds?: string[];
    language?: string;
  }
): Promise<IAcceptTermsResponse> => {
  const deviceInfo: IDeviceInfo = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };

  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/accept-terms`,
    {
      version: options?.version,
      documents: options?.documents,
      acceptedEntryIds: options?.acceptedEntryIds,
      language: options?.language ?? i18n.language,
      deviceInfo,
    }
  );

  return data.data;
};

/**
 * CIP readiness for the claimed link's user.
 * GET /widget/links/:linkId/cip-readiness
 *
 * The send-origination flow gates the payout-claim mint on this: minting requires a
 * Sender who completed CIP UI (pending / manual_review / verified; bind still
 * requires verified). The backend resolves their legal identity server-side.
 */
export interface ICipReadinessResponse {
  shouldRunCip: boolean;
  reason: string;
  cipTicketUrl: string | null;
  cipTicketId: string | null;
  missingFields?: string[];
}

export const checkCipReadiness = async (
  linkId: string
): Promise<ICipReadinessResponse> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/cip-readiness`);
  return data.data;
};

export interface ILegalDocumentResponse {
  html: string;
}

/**
 * Fetch a legal document (E-Sign, Terms, etc.) as ready-to-render HTML.
 * GET /widget/legal/:doc/:lang
 */
export const getLegalDocument = async (
  doc: string,
  lang: string
): Promise<ILegalDocumentResponse> => {
  const { data } = await widgetApi.get(`/widget/legal/${doc}/${lang}`);
  return data.data;
};

/**
 * Fetch the partner Terms & Conditions for the link in a specific language.
 * The backend serves manually authored per-language content and falls back to
 * English when the requested language is unavailable.
 * GET /widget/links/:linkId/terms?lang=xx
 */
export const getTerms = async (
  linkId: string,
  lang?: string
): Promise<ITermsBlock | null> => {
  const { data } = await widgetApi.get(`/widget/links/${linkId}/terms`, {
    params: lang ? { lang } : undefined,
  });
  return data.data?.terms ?? null;
};

type VgsAliasField = "card_number" | "card_cvc";

type VgsEchoBody = {
  json?: Partial<Record<VgsAliasField, string>>;
} & Partial<Record<VgsAliasField, string>>;

const pickAlias = (
  data: VgsEchoBody | null | undefined,
  field: VgsAliasField
): string | undefined => {
  const fromJson = data?.json?.[field];
  if (typeof fromJson === "string" && fromJson.length > 0) return fromJson;
  const top = data?.[field];
  if (typeof top === "string" && top.length > 0) return top;
  return undefined;
};

const isVgsAlias = (value: string | undefined): value is string =>
  typeof value === "string" && value.startsWith("tok_");

/**
 * Tokenize card data via the VGS inbound route.
 * POST https://{proxyHost}/post — proxyHost comes from claim config.vgs or
 * GET /widget/config/tokenization. Body is only { card_number, card_cvc }.
 */
export const tokenizeCard = async (
  pan: string,
  cvv: string
): Promise<{ numberToken: string; cvvToken: string }> => {
  const vgsProxyUrl = await vgsPostUrl();

  const { data } = await axios.post<VgsEchoBody>(
    vgsProxyUrl,
    { card_number: pan, card_cvc: cvv },
    { headers: { "Content-Type": "application/json" } }
  );

  const numberToken = pickAlias(data, "card_number");
  const cvvToken = pickAlias(data, "card_cvc");
  if (!isVgsAlias(numberToken) || !isVgsAlias(cvvToken)) {
    throw new Error("VGS_TOKENIZE_FAILED");
  }
  return { numberToken, cvvToken };
};

/** POST /widget/links/:linkId/payment-methods/cards/pre-validate */
export const preValidateCardPaymentMethod = async (
  linkId: string,
  card: IPreValidateCardRequest
): Promise<IPreValidateCardResult> => {
  const { data } = await widgetApi.post<
    IPreValidateCardResult | { success: boolean; data: IPreValidateCardResult }
  >(`/widget/links/${linkId}/payment-methods/cards/pre-validate`, card, {
    timeout: 40_000,
  });

  if (data && typeof data === "object" && "validationId" in data) {
    return data;
  }
  return (data as { data: IPreValidateCardResult }).data;
};

/** Tokenize a volatile CVV via VGS (no PAN). */
export const tokenizeCvv = async (cvv: string): Promise<string> => {
  const vgsProxyUrl = await vgsPostUrl();
  const { data } = await axios.post<VgsEchoBody>(
    vgsProxyUrl,
    { card_cvc: cvv },
    { headers: { "Content-Type": "application/json" } }
  );
  const token = pickAlias(data, "card_cvc");
  if (!isVgsAlias(token)) throw new Error("CVV_TOKENIZE_FAILED");
  return token;
};

/** POST /widget/links/:linkId/payment-methods/cards/:cardId/usage-readiness */
export const cardUsageReadiness = async (
  linkId: string,
  cardId: string,
  input: ICardUsageReadinessInput
): Promise<ICardUsageReadinessResult> => {
  const { data } = await widgetApi.post<{
    success: boolean;
    data: ICardUsageReadinessResult;
  }>(
    `/widget/links/${linkId}/payment-methods/cards/${cardId}/usage-readiness`,
    input
  );
  return data.data;
};

/** POST /widget/links/:linkId/payment-methods/cards/:cardId/provision */
export const provisionCard = async (
  linkId: string,
  cardId: string,
  input: ICardProvisionInput
): Promise<ICardProvisionResult> => {
  const { data } = await widgetApi.post<{
    success: boolean;
    data: ICardProvisionResult;
  }>(`/widget/links/${linkId}/payment-methods/cards/${cardId}/provision`, input, {
    timeout: 60_000,
  });
  return data.data;
};

/** PAN-only VGS tokenize fallback for register when numberToken is missing. */
export const tokenizeDestinationCard = async (
  pan: string
): Promise<{ numberToken: string }> => {
  const vgsProxyUrl = await vgsPostUrl();
  const { data } = await axios.post<VgsEchoBody>(
    vgsProxyUrl,
    { card_number: pan },
    { headers: { "Content-Type": "application/json" } }
  );
  const numberToken = pickAlias(data, "card_number");
  if (!isVgsAlias(numberToken)) throw new Error("VGS_TOKENIZE_FAILED");
  return { numberToken };
};

/**
 * Register a tokenized card as a funding payment method.
 * POST /widget/links/:linkId/payment-methods/cards
 */
export const addCardPaymentMethod = async (
  linkId: string,
  card: IAddCardPaymentMethodRequest
): Promise<ICardPaymentMethod> => {
  const { data } = await widgetApi.post(
    `/widget/links/${linkId}/payment-methods/cards`,
    card
  );
  return data.data;
};

/**
 * Get all payment methods for the user (cards, bank accounts, etc.).
 * GET /widget/links/:linkId/payment-methods
 */
export const getAllPaymentMethods = async (
  linkId: string
): Promise<IAllPaymentMethods> => {
  const { data } = await widgetApi.get(
    `/widget/links/${linkId}/payment-methods`
  );
  return data.data;
};

/** Pre-quote option shape used by catalog helpers. */
export interface IEstimateOption {
  capabilityKey: string;
  corridorCode: string;
  payerCode: string | null;
  payoutMethod: string;
  fundingMethod: string;
  sendAmount: number;
  receiveAmount: number;
  customerFee: number;
  customerFxRate: number;
  sourceCurrency: string;
  destinationCurrency: string;
}

/** Chain from GET /widget/operations/funding-locations (same as cash-deposit). */
export interface IFundingMerchant {
  merchantId: string;
  name: string;
  fee: number;
  fixedLoadAmounts: number[];
  states: string[];
  logoUrl: string;
}

export interface IFundingStoreAddress {
  line1: string;
  city: string;
  state: string;
  zip: string;
  latitude: number;
  longitude: number;
}

/** Physical store from GET /widget/operations/funding-locations/stores. */
export interface IFundingStore {
  storeId: string;
  merchantId: string;
  name: string;
  logoUrl: string;
  address: IFundingStoreAddress;
  distanceMiles?: number;
}

export interface IFundingStoresQuery {
  lat?: number;
  lng?: number;
  merchantId?: string;
}

/**
 * List cash-deposit funding locations (merchant chains) for the corridor.
 * GET /widget/operations/funding-locations
 */
export const getFundingLocations = async (
  state?: string
): Promise<IFundingMerchant[]> => {
  const { data } = await widgetApi.get("/widget/operations/funding-locations", {
    params: state ? { state } : undefined,
  });
  return data.data;
};

/**
 * Nearby physical stores for the locator map.
 * GET /widget/operations/funding-locations/stores
 */
export const getFundingStores = async (
  query: IFundingStoresQuery = {},
): Promise<IFundingStore[]> => {
  const params: Record<string, string> = {};
  if (query.lat != null && query.lng != null) {
    params.lat = String(query.lat);
    params.lng = String(query.lng);
  }
  if (query.merchantId) {
    params.merchantId = query.merchantId;
  }
  const { data } = await widgetApi.get(
    "/widget/operations/funding-locations/stores",
    { params },
  );
  return data.data;
};
